// Stop-edit draft + diff helpers. Extracted from
// useLogisticsRouteStopEdits.ts as the file-size-hygiene preamble for
// D3i (the hook was at 308 lines coming out of D3g UI -- inside the
// warning band -- and D3i adds a new quick-action + state widening,
// which the rule says requires a prep split first).
//
// Split-by-concern: this module owns the pure value-shape concerns
// (draft <-> stored stop conversion + per-field three-way diffing);
// the hook keeps the React state + the action callbacks that compose
// these helpers. No behavior change vs. having them inline.
import type { LogiStop, LogiStopKind } from "@/lib/logistics";

/** Edit-form draft shape. All fields are strings (UI input shape)
 * even where the stored value is a number / null / object -- the
 * diff helpers convert back on submit. stop_kind is read-only context
 * (the backend ignores it on update) but lives on the draft so the
 * conditional recipient inputs in the form can branch off it. */
export type StopEditDraft = {
  stop_kind: LogiStopKind;
  recipient_org_id_text: string;
  recipient_contact_id_text: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  scheduled_window_start_text: string;
  scheduled_window_end_text: string;
  arrived_at_text: string;
  departed_at_text: string;
  evidence_url_text: string;
  evidence_completed_at_text: string;
  notes_text: string;
};

/** Read a single address field from a stop's stored JSONB. Returns
 * "" for null/missing/non-string values so the draft input always
 * has a defined string to render. */
export function readAddressField(
  addr: Record<string, unknown> | null | undefined,
  key: string
): string {
  if (!addr) return "";
  const v = addr[key];
  return typeof v === "string" ? v : "";
}

/** Seed a fresh edit draft from the stored stop. The form's
 * "Apply suggestion" + per-field setters mutate the draft in place. */
export function draftFromStop(stop: LogiStop): StopEditDraft {
  return {
    stop_kind: stop.stop_kind,
    recipient_org_id_text: stop.recipient_org_id ?? "",
    recipient_contact_id_text: stop.recipient_contact_id ?? "",
    address_street: readAddressField(stop.address, "street"),
    address_city: readAddressField(stop.address, "city"),
    address_state: readAddressField(stop.address, "state"),
    address_postal_code: readAddressField(stop.address, "postal_code"),
    scheduled_window_start_text: stop.scheduled_window_start ?? "",
    scheduled_window_end_text: stop.scheduled_window_end ?? "",
    arrived_at_text: stop.arrived_at ?? "",
    departed_at_text: stop.departed_at ?? "",
    evidence_url_text: stop.evidence_url ?? "",
    evidence_completed_at_text: stop.evidence_completed_at ?? "",
    notes_text: stop.notes ?? "",
  };
}

/**
 * Three-way patcher for nullable string fields:
 *   undefined = leave alone (omit from payload),
 *   null      = clear,
 *   string    = set.
 *
 * Truth table:
 *   stored=null, draft=""  -> undefined  (no change, omit)
 *   stored=null, draft="X" -> "X"        (set)
 *   stored="X",  draft=""  -> null       (clear)
 *   stored="X",  draft="X" -> undefined  (no change, omit)
 *   stored="X",  draft="Y" -> "Y"        (set)
 *
 * Prefers null over "" so the backend explicitly clears rather than
 * rejecting an empty string on a column that disallows them.
 */
export function diffNullable(
  current: string | null,
  draftRaw: string
): string | null | undefined {
  const draft = draftRaw.trim();
  const cur = current ?? "";
  if (draft === cur) return undefined;
  return draft === "" ? null : draft;
}
